import uisFetch from './uisFetch';

// Rate limiter: max 10 requests per second, queue up to 2000
const RATE_LIMIT_PER_SECOND = 10;
const MAX_QUEUE_SIZE = 2000;
const WINDOW_MS = 1000;

/** Timestamps (ms) of requests dispatched within the current window */
const requestTimestamps = [];

/** Pending queue: array of { resolve } waiting to be dispatched */
const pendingQueue = [];

let drainScheduled = false;

/**
 * Drain the pending queue, dispatching requests as slots become available.
 * Reschedules itself until the queue is empty.
 */
function drainQueue() {
    drainScheduled = false;

    const now = Date.now();
    // Remove timestamps outside the current 1-second window
    while (requestTimestamps.length > 0 && now - requestTimestamps[0] >= WINDOW_MS) {
        requestTimestamps.shift();
    }

    // Dispatch as many queued requests as the remaining capacity allows
    while (pendingQueue.length > 0 && requestTimestamps.length < RATE_LIMIT_PER_SECOND) {
        const ts = Date.now();
        requestTimestamps.push(ts);
        const { resolve } = pendingQueue.shift();
        resolve();
    }

    // If there are still pending requests, schedule the next drain
    if (pendingQueue.length > 0 && !drainScheduled) {
        drainScheduled = true;
        // Wait until the oldest timestamp expires to free up a slot
        const oldest = requestTimestamps[0];
        const delay = WINDOW_MS - (Date.now() - oldest) + 1;
        setTimeout(drainQueue, Math.max(delay, 1));
    }
}

/**
 * Acquire a rate-limit slot.
 * Resolves immediately if under the limit, queues otherwise.
 * Rejects (returns false) if the queue is full.
 */
function acquireSlot() {
    const now = Date.now();
    // Remove stale timestamps
    while (requestTimestamps.length > 0 && now - requestTimestamps[0] >= WINDOW_MS) {
        requestTimestamps.shift();
    }

    if (requestTimestamps.length < RATE_LIMIT_PER_SECOND) {
        // Slot available immediately
        requestTimestamps.push(now);
        return Promise.resolve(true);
    }

    // No slot available — check queue capacity
    if (pendingQueue.length >= MAX_QUEUE_SIZE) {
        return Promise.resolve(false);
    }

    // Enqueue and schedule drain
    return new Promise((resolve) => {
        pendingQueue.push({ resolve: () => resolve(true) });
        if (!drainScheduled) {
            drainScheduled = true;
            const oldest = requestTimestamps[0];
            const delay = WINDOW_MS - (Date.now() - oldest) + 1;
            setTimeout(drainQueue, Math.max(delay, 1));
        }
    });
}

/**
 * Fetch a file from the given URL and convert it to a Base64 string.
 * Rate-limited to RATE_LIMIT_PER_SECOND requests per second.
 * Excess requests are queued (up to MAX_QUEUE_SIZE); beyond that, null is returned.
 * @param {string} url - The API endpoint to fetch the file.
 * @param {string} accessToken - The access token for authentication.
 * @returns {Promise<string|null>} - A Base64 string of the file or null if an error occurs.
 */
export const fetchAndConvertToBase64 = async (url, accessToken ) => {
    const allowed = await acquireSlot();
    if (!allowed) {
        console.warn("fetchAndConvertToBase64: rate limit queue full, dropping request:", url);
        return null;
    }

    try {
        const response = await uisFetch(accessToken, url, {
                method: "GET",
                headers: {
                }
            });
        if (response.ok) {
            const blob = await response.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } else {
            console.error("Failed to fetch file:", response.statusText);
            return null;
        }
    } catch (error) {
        console.error("Exception while fetching file:", error);
        return null;
    }
};